import getSchemaOfComp from './compSchemas';
import {generateError} from './errors';
import reporter from '../reporter';
import forEach from 'lodash/forEach';
import pickBy from 'lodash/pickBy';
import has from 'lodash/has';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isDate from 'lodash/isDate';
import isFunction from 'lodash/isFunction';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isInteger from 'lodash/isInteger';
import findIndex from 'lodash/findIndex';
import countBy from 'lodash/countBy';
import regexValidations from './regexValidations';
import {isExternalUrl} from 'privates/urlUtils';

const TYPES = {
    NUMBER: 'number',
    STRING: 'string',
    BOOLEAN: 'boolean',
    DATE: 'date',
    FUNCTION: 'function',
    ARRAY: 'array',
    OBJECT: 'object',
    URL: 'url',
    INTEGER: 'integer',
    UUID: 'uuid'
};

function validateArrayContainsItemsWithIds(items) {
    let res = true;

    if (!isArray(items)) {
        return false;
    }

    const idCounts = countBy(items, '_id');

    forEach(items, (item) => {
       if (!item._id) {
           reporter.reportSdkError(generateError.error_mandatory_id());
           res = false;
       }

       if (idCounts[item._id] !== 1) {
           reporter.reportSdkWarning(generateError.warning_duplicate_item({functionName: 'data', _id: item._id}));
       }
    });

    return res;
}

function validateArrayContainsItem(functionName, propertyName, item, arr) {
    const index = findIndex(arr, item);
    if (index > -1) {
        return true;
    }
    reporter.reportSdkError(generateError.error_item_not_found({
        functionName,
        propertyName,
        wrongValue: item[propertyName]
    }));
    return false;
}

function reportHttpUrls(value, propertyName, functionName) {
    const isHttpSiteReg = /(http):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    if (isHttpSiteReg.test(value)) {
        reporter.reportSdkWarning(generateError.warning_http_format({
            propertyName,
            functionName
        }));
    }
}

function validateByType(value, expectedType) {
    switch (expectedType) {
        case TYPES.NUMBER:
            return isNumber(value) && !isNaN(value);
        case TYPES.STRING:
            return isString(value);
        case TYPES.BOOLEAN:
            return isBoolean(value);
        case TYPES.DATE:
            return isDate(value) && !isNaN(value.getTime());
        case TYPES.FUNCTION:
            return isFunction(value);
        case TYPES.ARRAY:
            return isArray(value);
        case TYPES.OBJECT:
            return isObject(value) && !isArray(value) && !isFunction(value);
        case TYPES.URL:
            return isExternalUrl(value);
        case TYPES.INTEGER:
            return isInteger(value);
        case TYPES.UUID:
            return regexValidations.validateUUIDFormat(value);
        default:
            return true;
    }
}

function validateMandatoryFieldsAndReportIfNeeded(compType, functionName, dataToValidate, itemIndex) {
    let validationRes = {isValid: true};
    const expectedSchema = getSchemaOfComp(compType);
    if (!expectedSchema) {
        return validationRes;
    }
    const mandatoryFields = pickBy(expectedSchema, {mandatory: true});
    forEach(mandatoryFields, (v, k) => {
        if (!has(dataToValidate, k)) {
            validationRes = {isValid: false, prop: k};
            return;
        }
    });

    if (!validationRes.isValid) {
        const error = !isNil(itemIndex) ? generateError.error_mandatory_val_with_index : generateError.error_mandatory_val;
        reporter.reportSdkError(error({
            propertyName: validationRes.prop,
            functionName,
            index: itemIndex
        }));
    }

    return validationRes.isValid;
}

function validateLength(functionName, propertyName, value, minLen, maxLen, itemIndex, acceptNil) {
    const valid = isValueInRange(get(value, 'length'), minLen, maxLen, acceptNil);
    if(!valid){
        reporter.reportSdkError(getLengthError(minLen, maxLen, itemIndex)({
            functionName,
            propertyName,
            wrongValue: value,
            minLength: minLen,
            maxLength: maxLen,
            acceptedLength: maxLen,
            index: itemIndex
        }));
    }
    return valid;
}

function getLengthError(minLength, maxLength, itemIndex) {
    let error = isActualNumber(itemIndex) ? generateError.error_length_in_range_with_index : generateError.error_length_in_range;
    if(!isActualNumber(minLength) || !isActualNumber(maxLength)){
        return error;
    }

    if (minLength === maxLength) {
        error = isActualNumber(itemIndex) ? generateError.error_length_accept_single_value_with_index : generateError.error_length_accept_single_value;
    } else if (minLength === 0 && maxLength > 0) {
        error = isActualNumber(itemIndex) ? generateError.error_length_exceeds_with_index : generateError.error_length_exceeds;
    }
    return error;
}

function validateMinimum(functionName, propertyName, value, minVal, acceptNil) {
    if (acceptNil && isNil(value)) {
        return true;
    }
    if (!isActualNumber(value) || !isActualNumber(minVal) || value < minVal) {
        reporter.reportSdkError(generateError.error_larger_than({
            functionName,
            propertyName,
            wrongValue: value,
            minValue: minVal
        }));
        return false;
    }
    return true;
}

function validateMaximum(functionName, propertyName, value, maxValue, acceptNil) {
    if (acceptNil && isNil(value)) {
        return true;
    }
    if (!isActualNumber(value) || !isActualNumber(maxValue) || maxValue < value) {
        reporter.reportSdkError(generateError.error_less_than({
            functionName,
            propertyName,
            wrongValue: value,
            maxValue
        }));
        return false;
    }
    return true;
}

function validateRange(functionName, propertyName, value, minValue, maxValue, itemIndex, acceptNil) {
    const valid = isValueInRange(value, minValue, maxValue, acceptNil);
    if(!valid){
        reporter.reportSdkError(getRangeError(minValue, maxValue, itemIndex)({
            functionName,
            propertyName,
            wrongValue: value,
            minValue,
            maxValue,
            acceptedValue: minValue,
            index: itemIndex
        }));
    }
    return valid;
}

function isValueInRange(value, minValue, maxValue, acceptNil) {
    if (acceptNil && isNil(value)) {
        return true;
    }
    if (!isActualNumber(maxValue) || !isActualNumber(value) || !isActualNumber(minValue) ||
        maxValue < value || value < minValue) {
        return false;
    }
    return true;
}

function isActualNumber(value) {
    return isNumber(value) && !isNaN(value);
}

function getRangeError(minValue, maxValue, itemIndex) {
    let error = isActualNumber(itemIndex) ? generateError.error_range_with_index : generateError.error_range;
    if(!isActualNumber(minValue) || !isActualNumber(maxValue) || minValue < maxValue){
        return error;
    } else if (minValue === maxValue) {
        error = isActualNumber(itemIndex) ? generateError.error_accept_single_value_with_index : generateError.error_accept_single_value;
    } else if (minValue > maxValue) {
        error = isActualNumber(itemIndex) ? generateError.error_empty_component_with_index : generateError.error_empty_component;
    }
    return error;
}

function validateType(compType, functionName, propertyName, value, expectedType, warnIfNil, acceptNil, shouldCheckMandatoryFields, itemIndex) {
    if (isNil(value)) {
        if (warnIfNil) {
            const error = !isNil(itemIndex) ? generateError.warning_not_null_with_index : generateError.warning_not_null;
            reporter.reportSdkWarning(error({
                propertyName,
                functionName,
                index: itemIndex
            }));
        }
        if (acceptNil) {
            return true;
        }
    }

    let isValidByType = false;
    let actualType;
    expectedType.forEach((type) => {
        if (validateByType(value, type)) {
            isValidByType = true;
            actualType = type;
        }
    });
    if (!isValidByType) {
        const error = !isNil(itemIndex) ? generateError.error_type_with_index : generateError.error_type;
        reporter.reportSdkError(error({
            functionName,
            propertyName,
            wrongValue: value,
            expectedType: expectedType.join(','),
            index: itemIndex
        }));
        return false;
    }

    if (actualType === 'url' && includes(expectedType, 'url')) {
        reportHttpUrls(value, propertyName, functionName);
    }

    if (actualType === 'object' && includes(expectedType, 'object')) {
        const newObjType = isArray(compType) ? compType.concat([propertyName]) : [compType, propertyName];
        return validate(newObjType, functionName, value, shouldCheckMandatoryFields, itemIndex);
    }

    return true;
}

function validateValueFromEnum(functionName, propertyName, value, enumValues, itemIndex, acceptNil) {
    if (isNil(value) && acceptNil) {
        return true;
    }
    if (!includes(enumValues, value)) {
        const error = !isNil(itemIndex) ? generateError.error_type_with_index : generateError.error_type;
        reporter.reportSdkError(error({
            functionName,
            propertyName,
            wrongValue: value,
            expectedType: `from (${enumValues.join(',')})`,
            index: itemIndex
        }));
        return false;
    }
    return true;
}

function validateRegexForValue(functionName, propertyName, value, validationExpressions) {
    if (!regexValidations[validationExpressions](value)) {
        reporter.reportSdkError(generateError.error_bad_format({
            functionName,
            propertyName,
            wrongValue: value
        }));
        return false;
    }
    return true;
}

function validateRegex(functionName, propertyName, value, validationExpressions) {
    if (regexValidations[validationExpressions]) {
        if (isArray(value)) {
            return forEach(value, (val) => validateRegexForValue(functionName, propertyName, val, validationExpressions));
        }
        return validateRegexForValue(functionName, propertyName, value, validationExpressions);
    }
    return true;
}

function validate(compType, functionName, dataToValidate, shouldCheckMandatoryFields, itemIndex) {
    let res = true;
    const expectedSchema = getSchemaOfComp(compType);
    if (!expectedSchema) {
        return true;
    }
    if (shouldCheckMandatoryFields) {
        if (!validateMandatoryFieldsAndReportIfNeeded(compType, functionName, dataToValidate, itemIndex)) {
            return false;
        }
    }
    forEach(dataToValidate, (v, k) => {
        if (!res) {
            return;
        }
        const expectedSchema = getSchemaOfComp(compType)[k];
        if (!expectedSchema) {
            res = true;
            return;
        }
        const validations = expectedSchema.validations || {};

        if (validations.type) {
            if (!validateType(compType, functionName, k, v, expectedSchema.schemaType, expectedSchema.warnIfNil, expectedSchema.acceptNil, shouldCheckMandatoryFields, itemIndex)) {
                res = false;
                return;
            }
            if (expectedSchema.enum && !validateValueFromEnum(functionName, k, v, expectedSchema.enum, itemIndex, expectedSchema.acceptNil)) {
                res = false;
                return;
            }
        }
        if (validations.range) {
            if (!validateRange(functionName, k, v, expectedSchema.minVal, expectedSchema.maxVal, itemIndex, expectedSchema.acceptNil)) {
                res = false;
                return;
            }
        }
        if (validations.length) {
            if (!validateLength(functionName, k, v, expectedSchema.minLength, expectedSchema.maxLength, itemIndex, expectedSchema.acceptNil)) {
                res = false;
                return;
            }
        }
        if (validations.regex) {
            if (!validateRegex(functionName, k, v, expectedSchema.validationExpressions)) {
                res = false;
                return;
            }
        }

        if (validations.minimum) {
            if (!validateMinimum(functionName, k, v, expectedSchema.minVal, expectedSchema.acceptNil)) {
                res = false;
                return;
            }
        }

        if (validations.maximum) {
            if (!validateMaximum(functionName, k, v, expectedSchema.maxVal, expectedSchema.acceptNil)) {
                res = false;
                return;
            }
        }
    });
    return res;

}

export {
    TYPES,
    validate,
    validateRange,
    validateMinimum,
    validateMaximum,
    validateArrayContainsItem,
    validateValueFromEnum,
    validateByType,
    validateArrayContainsItemsWithIds,
    isValueInRange
};



// WEBPACK FOOTER //
// ./js/modules/privates/validations/validation.es6